/* eslint-disable react/no-unstable-nested-components, max-lines */
import { useState, useMemo } from 'react';
import { Select } from '@nazka/nazka.mapframe.components';
import { createColumnHelper } from '@tanstack/react-table';
import {
  faEye,
  faEyeSlash,
  faPencil,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@utils/i18n/i18n.consumers';
import { useAdaptationPlans } from '@services/query.service';
import { useDeletePlan, useUpdatePlanStatus } from '@services/mutation.service';

import AdminTable from '../AdminTable/AdminTable';
import AdminHazardTags from '../AdminHazardTags/AdminHazardTags';
import AdminStatusBadge from '../AdminStatusBadge/AdminStatusBadge';
import AdminActions from '../AdminActions/AdminActions';
import AdminConfirmationDialog from '../AdminConfirmationDialog/AdminConfirmationDialog';

import { STATUS_FILTER_OPTIONS } from '../admin.constants';

import './admin-plans.styl';

const columnHelper = createColumnHelper();

function AdminPlans() {
  const [deleteAdaptationPlan, setDeleteAdaptationPlan] = useState(null);
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_OPTIONS[0]);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: adaptationPlans } = useAdaptationPlans();

  const updatePlanStatus = useUpdatePlanStatus();
  const deleteAdaptationPlanMutation = useDeletePlan();

  const columns = [
    columnHelper.accessor('translations.en.title', {
      header: 'Title',
      cell: info => (
        <span className="story-title">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: info => <AdminStatusBadge status={info.getValue()?.toLowerCase()} />,
    }),
    columnHelper.accessor('createdAt', {
      header: 'Creation date',
      cell: (info) => {
        const date = info.getValue();
        return date ? new Date(date).toLocaleDateString('nl-BE') : '-';
      },
    }),
    columnHelper.accessor('hazardCategories', {
      header: 'Linked hazard',
      enableSorting: false,
      cell: info => <AdminHazardTags hazards={info.getValue()} />,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      meta: { width: '200px' },
      cell: (info) => {
        const adaptationPlan = info.row.original;
        const isPublished = adaptationPlan.status?.toLowerCase() === 'published';

        return (
          <AdminActions
            actions={[
              {
                icon: isPublished ? faEyeSlash : faEye,
                title: isPublished ? t('Unpublish plan') : t('Publish plan'),
                onClick: () => updatePlanStatus.mutate({ id: adaptationPlan.id, status: isPublished ? 'unpublish' : 'publish' }),
              },
              {
                icon: faPencil,
                title: t('Edit plan'),
                onClick: () => navigate(`/admin/plans/${adaptationPlan.id}`),
              },
              {
                icon: faTrash,
                title: t('Delete plan'),
                onClick: () => setDeleteAdaptationPlan({ title: adaptationPlan.translations.en.title, id: adaptationPlan.id }),
                iconColor: 'red',
              }]}
          />
        );
      },
    }),
  ];

  const filteredData = useMemo(() => {
    if (!adaptationPlans) {
      return [];
    }

    if (statusFilter?.id === 'all') {
      return adaptationPlans;
    }

    return adaptationPlans.filter(story => story.status?.toLowerCase() === statusFilter?.id.toLowerCase());
  }, [adaptationPlans, statusFilter]);

  return (
    <div className="admin-plans">
      <h2>
        {t('Adaptation plans')}
      </h2>
      <div className="admin-plans__filter">
        <Select
          options={STATUS_FILTER_OPTIONS}
          value={statusFilter}
          onInputChange={value => setStatusFilter(value)}
          className="admin-plans__filter__status"
          width="180px"
        />
      </div>
      <AdminTable
        data={filteredData}
        columns={columns}
        initialSorting={[{ id: 'createdAt', desc: true }]}
      />
      {deleteAdaptationPlan && (
        <AdminConfirmationDialog
          title={t('Delete plan')}
          helpText={t('Do you really want to delete {{title}}? This action can not be reversed.', { title: deleteAdaptationPlan.title })}
          cancelText={t('Cancel')}
          confirmText={t('Yes, delete')}
          onConfirm={async () => {
            await deleteAdaptationPlanMutation.mutateAsync({ id: deleteAdaptationPlan.id });
            setDeleteAdaptationPlan(null);
          }}
          onCancel={() => setDeleteAdaptationPlan(null)}
          isPending={deleteAdaptationPlanMutation.isPending}
        />
      )}
    </div>
  );
}

export default AdminPlans;